import {
  CircularProgress,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import axios from 'axios';
import { useFormik } from 'formik';
import Link from 'next/link';
import * as React from 'react';
import * as Yup from 'yup';

import { CONTACT_EMAIL } from '@/lib/constants';

import MainButton from '@/components/buttons/MainButton';
import InputField from '@/components/forms/InputField';

const LOOKING_TO = {
  grow_audience: 'Grow my audience via clips',
  repurpose: 'Repurpose my existing content',
  speed: 'Make clips faster',
  money: 'Save money',
  editor_avoid: 'Avoid hiring an editor',
  editor_focus: 'Have my editor work on other content',
  editor_time: 'Save my editor time',
};

const WaitlistProfileForm: React.FC<{
  email?: string;
  onSubmit?: (values: any) => void;
  submitButtonText?: string | React.ReactElement;
}> = ({ email, onSubmit, submitButtonText: submitText }) => {
  const formik = useFormik({
    initialValues: {
      name: '',
      email: email,
      channel: '',
      creator_type: '',
      audience_size: '',
      goals: [],
      notes: '',
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required('Please enter your name'),
      email: Yup.string().email().required('Please enter an email'),
      creator_type: Yup.string().required('Please choose a creator type'),
    }),
    onSubmit: async (data, { setStatus }) => {
      try {
        await axios.post('/api/signup', {
          ...data,
        });
        setStatus('success');
        onSubmit?.(data);
      } catch (error) {
        setStatus('error');
        throw error;
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className='mx-auto max-w-xl pt-4'>
      <div className='flex w-full flex-col gap-4 text-left'>
        <div className='flex flex-col gap-1'>
          <InputLabel id='demo-simple-select-label'>Your name*</InputLabel>
          <InputField
            name='name'
            focusOnLoad
            value={formik.values.name}
            onChange={formik.handleChange}
            placeholder='Enter your name here...'
          />
        </div>
        <div className='flex flex-col gap-1'>
          <InputLabel id='demo-simple-select-label'>Your email*</InputLabel>
          <InputField
            name='email'
            type='email'
            value={formik.values.email}
            onChange={formik.handleChange}
            placeholder='Enter your email here...'
          />
        </div>
        <div className='flex flex-col gap-1'>
          <InputLabel id='demo-simple-select-label'>
            Link to your channel*
          </InputLabel>
          <InputField
            name='channel'
            value={formik.values.channel}
            onChange={formik.handleChange}
            placeholder='Paste a link here...'
          />
        </div>
        <div className='flex flex-col gap-1'>
          <InputLabel id='demo-simple-select-label'>I am a*</InputLabel>
          <TextField
            id='demo-simple-select'
            name='creator_type'
            value={formik.values.creator_type}
            label={formik.values.creator_type ? null : 'Select'}
            select
            onChange={formik.handleChange}
            InputLabelProps={{
              shrink: false,
            }}
            className='bg-white'
          >
            <MenuItem value='podcaster'>Podcaster</MenuItem>
            <MenuItem value='video_editor'>Video Editor</MenuItem>
            <MenuItem value='agency'>Agency</MenuItem>
            <MenuItem value='gamer'>Gamer</MenuItem>
            <MenuItem value='other'>Other</MenuItem>
            <MenuItem value='just_curious'>I'm just curious</MenuItem>
          </TextField>
        </div>
        <div className='flex flex-col gap-1'>
          <InputLabel id='demo-simple-select-label'>Audience size*</InputLabel>
          <TextField
            id='demo-simple-select'
            name='audience_size'
            value={formik.values.audience_size}
            label={formik.values.audience_size ? null : 'Select'}
            select
            onChange={formik.handleChange}
            InputLabelProps={{
              shrink: false,
            }}
            className='bg-white'
          >
            <MenuItem value='small'>0 - 10,000</MenuItem>
            <MenuItem value='medium'>10,000 - 100,000</MenuItem>
            <MenuItem value='large'>100,000+</MenuItem>
          </TextField>
        </div>
        <div className='flex flex-col gap-1'>
          <InputLabel id='demo-simple-select-label'>I'm looking to</InputLabel>
          <Select
            name='goals'
            value={formik.values.goals}
            label={formik.values.goals ? null : 'Select'}
            multiple
            onChange={formik.handleChange}
            className='bg-white'
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) {
                return (
                  <span className='text-gray-500'>Select one or more</span>
                );
              }

              return selected.map((v) => LOOKING_TO[v]).join(', ');
            }}
          >
            {Object.keys(LOOKING_TO).map((key, i) => (
              <MenuItem key={key} value={key}>
                {LOOKING_TO[key as keyof typeof LOOKING_TO]}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className='flex flex-col gap-1'>
          <InputLabel>Anything else?</InputLabel>
          <InputField
            name='notes'
            value={formik.values.notes}
            onChange={formik.handleChange}
            placeholder='About you, your channel, your goals...'
          />
        </div>
        {Object.keys(formik.errors).map((key) =>
          formik.touched[key as keyof typeof formik.touched] ? (
            <div className='ml-2 text-xs text-red-600'>
              {formik.errors[key as keyof typeof formik.errors]}
            </div>
          ) : null
        )}
        <div className='space-y-3'>
          {formik.status === 'success' ? (
            <div>
              <h5>
                Thanks! We'll reach out to <strong>{email}</strong> when it's
                time to get started.
              </h5>
              <Link legacyBehavior href='/'>
                <a>Back home &rarr;</a>
              </Link>
            </div>
          ) : (
            <>
              {formik.status === 'error' ? (
                <div>
                  <h5>
                    Something went wrong. Please try again or get in touch at{' '}
                    <strong>{CONTACT_EMAIL}</strong> so we can get things fixed.
                  </h5>
                </div>
              ) : null}
              <MainButton className='rounded py-4 px-8 font-bold text-white'>
                {formik.isSubmitting ? (
                  <>
                    <CircularProgress
                      size={14}
                      thickness={4}
                      sx={{ mt: 0.5, color: 'white' }}
                    />
                    &nbsp; Submitting...
                  </>
                ) : (
                  submitText || '🚀 Submit'
                )}
              </MainButton>
            </>
          )}
        </div>
      </div>
    </form>
  );
};

export default WaitlistProfileForm;
